import { cleaningTaskRepository, habitRepository, logRepository } from '../db/database';
import type { CleaningTask, Habit, IsoDate } from '../models/types';
import { addDays, daysBetween } from '../utils/date';

/**
 * Temizlik mantığı — CLAUDE.md §7.5.
 *
 * İki işi var:
 * 1. Bir görevin ne zaman "sırası geldiğini" hesaplamak.
 * 2. Gün sonundaki otomatik "Temizlik" alışkanlığı kuralını uygulamak.
 *
 * Alt kısımdaki hesaplar saf fonksiyondur (veritabanına dokunmaz),
 * böylece hem ekran hem gün kapanışı aynı formülü kullanır ve ikisi
 * birbirinden sapamaz.
 */

/* ==================================================================
   Görev zamanlaması (saf hesaplar)
   ================================================================== */

/**
 * Görevin bir sonraki yapılma tarihi.
 *
 * Hiç yapılmamış görevlerde `null` döner; bunlar "hemen sırası gelmiş"
 * sayılır — kullanıcı görevi eklediyse yapılmasını bekliyordur.
 */
export function getTaskDueDate(task: CleaningTask): IsoDate | null {
  if (task.lastCompletedDate === null) {
    return null;
  }
  return addDays(task.lastCompletedDate, task.cycleDays);
}

/**
 * Görevin belirli bir günde sırasının gelip gelmediği.
 * "Sırası geldi" = yapılma tarihi o güne eşit ya da ondan önce.
 */
export function isTaskDue(task: CleaningTask, date: IsoDate): boolean {
  const dueDate = getTaskDueDate(task);
  // Hiç yapılmamış görev her zaman bekliyordur.
  if (dueDate === null) return true;
  return dueDate <= date;
}

/** Görevin ekrandaki durumu (§7.5). */
export type TaskUrgency = 'clean' | 'due' | 'overdue';

export interface TaskSchedule {
  urgency: TaskUrgency;
  /** Kullanıcıya gösterilecek Türkçe etiket. */
  label: string;
}

/**
 * Görev şu anda "temiz" mi — yani döngüsü sürüyor mu?
 *
 * Yeşil tık, işaretlendiği gün değil TÜM DÖNGÜ boyunca yanık kalır:
 * kullanıcı ev haritasına bakınca hangi işlerin hâlâ temiz olduğunu
 * tek bakışta görmeli (§7.5). "Temiz olmak" ile "sırası gelmemiş
 * olmak" aynı şeydir, o yüzden tek bir hesaptan türetiliyor.
 */
export function isTaskClean(task: CleaningTask, date: IsoDate): boolean {
  return !isTaskDue(task, date);
}

/**
 * Bir görevin durum etiketini üretir (§7.5).
 *
 * - `clean`   : yeşil "5 gün"  — döngü sürüyor, kalan gün sayısı.
 * - `due`     : kırmızı "Bekliyor" — tam bugün yapılmalı.
 * - `overdue` : kırmızı "3 gün" — üzerinden geçen gün sayısı.
 *
 * "5 gün" hem kalan hem geçen süre için kullanılıyor; ikisini RENK
 * ayırıyor (yeşil = kaldı, kırmızı = geçti).
 */
export function getTaskSchedule(task: CleaningTask, today: IsoDate): TaskSchedule {
  const dueDate = getTaskDueDate(task);

  // Hiç yapılmamış görevin gecikme günü hesaplanamaz — ne zamandan
  // beri beklediği bilinmiyor, sadece beklediği biliniyor.
  if (dueDate === null) {
    return { urgency: 'overdue', label: 'Bekliyor' };
  }

  const daysRemaining = daysBetween(today, dueDate);

  if (daysRemaining > 0) {
    return { urgency: 'clean', label: `${daysRemaining} gün` };
  }
  if (daysRemaining === 0) {
    return { urgency: 'due', label: 'Bekliyor' };
  }
  return { urgency: 'overdue', label: `${Math.abs(daysRemaining)} gün` };
}

/** Görev tam olarak bu gün işaretlendi mi? */
export function isTaskCompletedOn(task: CleaningTask, date: IsoDate): boolean {
  return task.lastCompletedDate === date;
}

/**
 * §7.5'in gün sonu kuralı: o gün sırası gelmiş hiçbir görev kalmadıysa
 * "Temizlik" alışkanlığı yapılmış sayılır.
 *
 * Dikkat: hiç temizlik görevi tanımlanmamışsa da sonuç `true` olur
 * (sırası gelmiş görev yoktur). Kural birebir böyle yazılmış; ama
 * pratikte "Temizlik" alışkanlığı varken hiç görev olmaması, her gün
 * bedava puan demektir.
 */
export function isCleaningSatisfied(tasks: CleaningTask[], date: IsoDate): boolean {
  return !tasks.some((task) => isTaskDue(task, date));
}

/* ==================================================================
   Otomatik alışkanlık kaydı
   ================================================================== */

/**
 * Temizlik kuralına BAĞLI otomatik alışkanlığı bulur.
 *
 * `kind === 'auto'` diye aramak yetmez: ileride başka otomatik
 * alışkanlıklar da olacak (başka kurallar tarafından hesaplanan).
 * Kaynağa göre aramak, temizlik durumunun yanlış alışkanlığa
 * yazılmasını imkânsız kılar.
 */
function findCleaningHabit(habits: Habit[]): Habit | undefined {
  return habits.find(
    (habit) => habit.kind === 'auto' && habit.autoSource === 'cleaning' && !habit.archived,
  );
}

/**
 * "Temizlik" alışkanlığının o güne ait kaydını hesaplayıp yazar.
 *
 * Neden normal bir `HabitLog` olarak yazıyoruz: böylece puanlama
 * motoru (§7.2) otomatik alışkanlığı diğerlerinden ayırt etmek
 * zorunda kalmıyor — onun için de sadece "kaydı olan bir alışkanlık".
 * Kayıt türetilmiş bir değerdir, bu yüzden her çağrıda üzerine
 * yazılması güvenlidir.
 *
 * İki yerden çağrılır:
 * - Anasayfa açılırken (kullanıcı günün güncel durumunu görsün diye)
 * - Gün kapanırken (nihai değeri deftere girsin diye)
 */
export async function syncAutomaticCleaningLog(date: IsoDate): Promise<void> {
  const [habits, tasks] = await Promise.all([
    habitRepository.listActive(),
    cleaningTaskRepository.list(),
  ]);

  const cleaningHabit = findCleaningHabit(habits);
  // Kullanıcı temizliğe bağlı alışkanlığı silmişse yazacak bir şey yok.
  if (!cleaningHabit) return;

  await logRepository.upsert({
    id: `${cleaningHabit.id}:${date}`,
    habitId: cleaningHabit.id,
    date,
    completionRatio: isCleaningSatisfied(tasks, date) ? 1 : 0,
  });
}
